import * as turf from '@turf/turf';
import MapboxContext from '@sofarocean/mapbox-context/dist/contexts/MapboxContext';
import { some } from 'lodash';
import { GeoJSONSource, LngLatLike, MapMouseEvent } from 'mapbox-gl';
import { useCallback, useContext, useEffect, useRef } from 'react';

import { LocationData } from '@/common/types';
import {
    GEOFENCE_LINE_DASHARRAY,
    GEOFENCE_LINE_WIDTH,
    addOrUpdateGeoJSONLayer,
    createGeofenceGeometries,
    getGeofenceColors,
} from '@/components/map/layers/geofenceUtils';
import { MAX_GEOFENCE_RADIUS_METERS } from '@/components/spotter/notificationTab/GeofenceSection';
import { isSpotterOutOfGeofence } from '@/hooks/useGeofenceDisplayData';
import { lngLat, lngLatToTurf } from '@/utils';

export type GeofenceLayerProps = {
    radius: number; // Radius in meters
    center: { lng: number; lat: number };
    setCenter: (center: { lng: number; lat: number }) => void;
    centerMapTrigger?: number; // Timestamp to trigger map centering
    setRadius: (radiusMeters: number) => void;
    readOnly?: boolean;
    spotterLocation?: LocationData;
};

type GeofenceMouseTarget = 'None' | 'Geofence' | 'Radius';

const circleId = 'geofence-circle-line';
const circleFillId = 'geofence-circle-fill';
const radiusLineId = 'geofence-radius-line';
const centerMarkId = 'geofence-center-mark';
const radiusGrabberId = 'geofence-radius-grabber-mark';
const layerIds = [circleFillId, circleId, radiusLineId, centerMarkId, radiusGrabberId];

const hoverCursor = (target: GeofenceMouseTarget) => {
    switch (target) {
        case 'None':
            return '';
        case 'Geofence':
            return 'grab';
        case 'Radius':
            return 'ew-resize';
    }
};

const activeCursor = (target: GeofenceMouseTarget) => {
    switch (target) {
        case 'None':
            return '';
        case 'Geofence':
            return 'grabbing';
        case 'Radius':
            return 'ew-resize';
    }
};

export const GeofenceLayer = ({
    radius,
    center,
    setCenter,
    centerMapTrigger,
    setRadius,
    readOnly,
    spotterLocation,
}: GeofenceLayerProps) => {
    const { map } = useContext(MapboxContext);
    const isDraggingRef = useRef<GeofenceMouseTarget>('None');
    const draggingOffset = useRef<{ latOffset: number; lonOffset: number }>();

    const updateGeofencePosition = useCallback(
        (newCenter: LngLatLike, newRadius: number) => {
            if (!map) return;

            const { circle, radiusLine, crossmark, radiusGrabber } = createGeofenceGeometries(newCenter, newRadius);

            // Update all sources
            const fillSource = map.getSource(circleFillId) as GeoJSONSource;
            const lineSource = map.getSource(circleId) as GeoJSONSource;
            const radiusSource = map.getSource(radiusLineId) as GeoJSONSource;
            const centerSource = map.getSource(centerMarkId) as GeoJSONSource;
            const radiusGrabberSource = map.getSource(radiusGrabberId) as GeoJSONSource;

            if (fillSource) fillSource.setData(circle);
            if (lineSource) lineSource.setData(circle);
            if (radiusSource) radiusSource.setData(radiusLine);
            if (centerSource) centerSource.setData(crossmark);
            if (radiusGrabberSource) radiusGrabberSource.setData(radiusGrabber);

            // Update colors based on breach status
            if (spotterLocation) {
                const centerLatLng = lngLat(newCenter);
                const { isBreached } = isSpotterOutOfGeofence(centerLatLng, newRadius, lngLat(spotterLocation));
                const { fillColor, lineColor, fillOpacity } = getGeofenceColors(isBreached);

                if (map.getLayer(circleFillId)) {
                    map.setPaintProperty(circleFillId, 'fill-color', fillColor);
                    map.setPaintProperty(circleFillId, 'fill-opacity', fillOpacity);
                }
                if (map.getLayer(circleId)) {
                    map.setPaintProperty(circleId, 'line-color', lineColor);
                }
                if (map.getLayer(radiusLineId)) {
                    map.setPaintProperty(radiusLineId, 'line-color', lineColor);
                }
                if (map.getLayer(centerMarkId)) {
                    map.setPaintProperty(centerMarkId, 'line-color', lineColor);
                }
            }
        },
        [map, spotterLocation]
    );

    // Check if cursor is over any geofence layer
    const geofenceMouseTarget = useCallback(
        (e: MapMouseEvent): GeofenceMouseTarget => {
            const features = e.target.queryRenderedFeatures(e.point, { layers: layerIds });
            if (readOnly || features.length === 0) {
                return 'None';
            }
            if (some(features, (f) => f.layer?.id === radiusGrabberId)) {
                return 'Radius';
            }
            return 'Geofence';
        },
        [readOnly]
    );

    const centerFromEvent = (e: MapMouseEvent) => ({
        lng: e.lngLat.lng - (draggingOffset.current?.lonOffset ?? 0),
        lat: e.lngLat.lat - (draggingOffset.current?.latOffset ?? 0),
    });

    const radiusFromEvent = useCallback(
        (e: MapMouseEvent) =>
            Math.min(
                turf.distance(lngLatToTurf({ lat: center.lat, lng: e.lngLat.lng }), lngLatToTurf(center), {
                    units: 'meters',
                }),
                MAX_GEOFENCE_RADIUS_METERS
            ),
        [center]
    );

    const onMouseDown = useCallback(
        (e: MapMouseEvent) => {
            const target = geofenceMouseTarget(e);
            if (target === 'None') return;

            e.preventDefault();
            isDraggingRef.current = target;
            e.target.dragPan.disable();
            draggingOffset.current = { lonOffset: e.lngLat.lng - center.lng, latOffset: e.lngLat.lat - center.lat };
            const canvas = e.target.getCanvas();
            if (canvas) {
                canvas.style.cursor = activeCursor(target);
            }
        },
        [center.lat, center.lng, geofenceMouseTarget]
    );

    const onMouseMove = useCallback(
        (e: MapMouseEvent) => {
            if (isDraggingRef.current === 'None') {
                const canvas = e.target.getCanvas();
                if (!canvas) {
                    return;
                }
                // Update cursor when hovering over geofence
                canvas.style.cursor = hoverCursor(geofenceMouseTarget(e));
            } else if (isDraggingRef.current === 'Geofence') {
                // Update geofence position during drag
                const newCenter = centerFromEvent(e);
                updateGeofencePosition(newCenter, radius);
            } else if (isDraggingRef.current === 'Radius') {
                // Update geofence position during drag
                const newRadius = radiusFromEvent(e);
                updateGeofencePosition(center, newRadius);
            }
        },
        [center, radius, radiusFromEvent, updateGeofencePosition, geofenceMouseTarget]
    );

    const onMouseUp = useCallback(
        (e: MapMouseEvent) => {
            if (isDraggingRef.current === 'None') return;

            e.target.dragPan.enable();
            const canvas = e.target.getCanvas();
            if (canvas) {
                canvas.style.cursor = '';
            }

            if (isDraggingRef.current === 'Geofence') {
                setCenter(centerFromEvent(e));
            } else if (isDraggingRef.current === 'Radius') {
                setRadius(radiusFromEvent(e));
            }

            isDraggingRef.current = 'None';
        },
        [radiusFromEvent, setCenter, setRadius]
    );

    useEffect(() => {
        if (!map || !centerMapTrigger) {
            return;
        }

        map.flyTo({
            center,
            zoom: 14,
            duration: 500,
        });
        /*
         This dependency array does not include `center` intentionally because we want to move the map only when `centerMapTrigger` changes
         and not on every change of `center`. Otherwise, the map will move every drag of the geofence
         */
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [map, centerMapTrigger]);

    useEffect(() => {
        if (!map) {
            return;
        }

        // Create initial geometries
        const { circle, radiusLine, crossmark, radiusGrabber } = createGeofenceGeometries(center, radius);

        // Determine initial breach status
        const isBreached = spotterLocation
            ? isSpotterOutOfGeofence(center, radius, lngLat(spotterLocation))?.isBreached
            : false;

        const { fillColor, lineColor, fillOpacity } = getGeofenceColors(isBreached);

        // Add circle fill layer
        addOrUpdateGeoJSONLayer(
            map,
            { sourceId: circleFillId },
            {
                id: circleFillId,
                type: 'fill',
                source: circleFillId,
                layout: {},
                paint: {
                    'fill-color': fillColor,
                    'fill-opacity': fillOpacity,
                },
            },
            circle
        );

        // Add circle outline layer
        addOrUpdateGeoJSONLayer(
            map,
            { sourceId: circleId },
            {
                id: circleId,
                type: 'line',
                source: circleId,
                layout: {},
                paint: {
                    'line-color': lineColor,
                    'line-width': GEOFENCE_LINE_WIDTH,
                    'line-dasharray': GEOFENCE_LINE_DASHARRAY,
                },
            },
            circle
        );

        // Add radius line layer
        addOrUpdateGeoJSONLayer(
            map,
            { sourceId: radiusLineId },
            {
                id: radiusLineId,
                type: 'line',
                source: radiusLineId,
                layout: {},
                paint: {
                    'line-color': lineColor,
                    'line-width': GEOFENCE_LINE_WIDTH,
                },
            },
            radiusLine
        );

        // Add center mark layer
        addOrUpdateGeoJSONLayer(
            map,
            { sourceId: centerMarkId },
            {
                id: centerMarkId,
                type: 'line',
                source: centerMarkId,
                layout: {},
                paint: {
                    'line-color': lineColor,
                    'line-width': 3,
                },
            },
            crossmark
        );

        // Add radius grabber layer
        addOrUpdateGeoJSONLayer(
            map,
            { sourceId: radiusGrabberId },
            {
                id: radiusGrabberId,
                type: 'fill',
                source: radiusGrabberId,
                layout: {},
                paint: {
                    'fill-color': fillColor,
                    'fill-opacity': 0,
                },
            },
            radiusGrabber
        );

        map.on('mousedown', onMouseDown);
        map.on('mousemove', onMouseMove);
        map.on('mouseup', onMouseUp);

        return () => {
            // Remove event listeners
            map.off('mousedown', onMouseDown);
            map.off('mousemove', onMouseMove);
            map.off('mouseup', onMouseUp);

            map.dragPan.enable();
            isDraggingRef.current = 'None';
        };
    }, [map, center, radius, setCenter, onMouseDown, onMouseMove, onMouseUp, spotterLocation]);

    return null;
};
